import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  ListSubheader,
  Alert,
  Divider,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { default as DeleteIcon } from '@mui/icons-material/Delete';
import { default as AddCircleOutlineIcon } from '@mui/icons-material/AddCircleOutline';
import type { BulkAnimalData, AnimalYield } from '../../../../types/animal';

interface AnimalCategory {
  category: string;
  types: { name: string }[];
}

interface BulkAnimalDialogProps {
  open: boolean;
  onClose: () => void;
  bulkAnimalData: BulkAnimalData;
  setBulkAnimalData: (data: BulkAnimalData) => void;
  handleBulkInputChange: (field: keyof BulkAnimalData, value: any) => void;
  animalCategories: AnimalCategory[];
  availableBreeds: string[];
  customBreedName: string;
  handleBulkCustomBreedInput: (value: string) => void;
  handleSaveBulkAnimals: () => void;
  animalStatuses: string[];
  animalPurposes: string[];
  handleAddBulkYield: () => void;
  handleRemoveBulkYield: (index: number) => void;
  handleBulkYieldChange: (index: number, field: keyof AnimalYield, value: any) => void;
}

const BulkAnimalDialog: React.FC<BulkAnimalDialogProps> = ({
  open,
  onClose,
  bulkAnimalData,
  setBulkAnimalData,
  handleBulkInputChange,
  animalCategories,
  availableBreeds,
  customBreedName,
  handleBulkCustomBreedInput,
  handleSaveBulkAnimals,
  animalStatuses,
  animalPurposes,
  handleAddBulkYield,
  handleRemoveBulkYield,
  handleBulkYieldChange,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>Bulk Add Animals</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Add multiple animals at once with similar characteristics. Individual details can be updated later.
            </Alert>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Animal Type"
              value={bulkAnimalData.animalType}
              onChange={(e) => handleBulkInputChange('animalType', e.target.value)}
              required
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                },
              }}
            >
              {animalCategories.map((category) => [
                <ListSubheader key={category.category}>
                  {category.category}
                </ListSubheader>,
                ...category.types.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))
              ])}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Breed"
              value={bulkAnimalData.breed}
              onChange={(e) => handleBulkInputChange('breed', e.target.value)}
              required
              disabled={availableBreeds.length === 0}
            >
              {availableBreeds.map((breed) => (
                <MenuItem key={breed} value={breed}>
                  {breed}
                </MenuItem>
              ))}
              <MenuItem value="Custom Breed">
                <em>Custom Breed (specify below)</em>
              </MenuItem>
            </TextField>
          </Grid>
          {bulkAnimalData.breed === 'Custom Breed' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Custom Breed Name"
                value={customBreedName}
                onChange={(e) => handleBulkCustomBreedInput(e.target.value)}
                placeholder="Enter a custom breed name"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Gender"
              value={bulkAnimalData.gender}
              onChange={(e) => handleBulkInputChange('gender', e.target.value)}
              required
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="mixed">Mixed (for multiple animals)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Birth Date"
              value={bulkAnimalData.birthDate}
              onChange={(e) => handleBulkInputChange('birthDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Acquisition Date (if different)"
              value={bulkAnimalData.acquired || ''}
              onChange={(e) => handleBulkInputChange('acquired', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="When these animals were acquired if not born on farm"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={bulkAnimalData.status}
              onChange={(e) => handleBulkInputChange('status', e.target.value)}
              required
            >
              {animalStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Color/Markings"
              value={bulkAnimalData.color || ''}
              onChange={(e) => handleBulkInputChange('color', e.target.value)}
              placeholder="E.g., Black & White, Brown Spotted"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Average Weight"
              value={bulkAnimalData.averageWeight}
              onChange={(e) => handleBulkInputChange('averageWeight', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Weight Unit"
              value={bulkAnimalData.weightUnit}
              onChange={(e) => handleBulkInputChange('weightUnit', e.target.value)}
            >
              <MenuItem value="kg">Kilogram (kg)</MenuItem>
              <MenuItem value="lb">Pound (lb)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Purchase Price per Animal (Ksh)"
              value={bulkAnimalData.purchasePrice || ''}
              onChange={(e) => handleBulkInputChange('purchasePrice', parseFloat(e.target.value))}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Current Value per Animal (Ksh)"
              value={bulkAnimalData.currentValue || ''}
              onChange={(e) => handleBulkInputChange('currentValue', parseFloat(e.target.value))}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Purpose"
              value={bulkAnimalData.purpose || ''}
              onChange={(e) => handleBulkInputChange('purpose', e.target.value)}
              helperText="Primary purpose for raising these animals"
            >
              {animalPurposes.map((purpose) => (
                <MenuItem key={purpose} value={purpose}>
                  {purpose.charAt(0).toUpperCase() + purpose.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Dynamic Yields Section */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Animal Yields (Apply to all)</Typography>
              <Button 
                startIcon={<AddCircleOutlineIcon />} 
                size="small" 
                onClick={handleAddBulkYield}
              >
                Add Yield Type
              </Button>
            </Box>
            {(!bulkAnimalData.yields || bulkAnimalData.yields.length === 0) ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                No yields added yet.
              </Typography>
            ) : (
              bulkAnimalData.yields.map((yieldItem, index) => (
                <Box key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Yield Type (e.g. Milk, Eggs, Meat)"
                        value={yieldItem.yieldType}
                        onChange={(e) => handleBulkYieldChange(index, 'yieldType', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Yield Unit (e.g. L, kg)"
                        value={yieldItem.yieldUnit}
                        onChange={(e) => handleBulkYieldChange(index, 'yieldUnit', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Daily Avg Yield"
                        value={yieldItem.dailyAvgYield}
                        onChange={(e) => handleBulkYieldChange(index, 'dailyAvgYield', Number(e.target.value))}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Market Price per Yield Unit"
                        value={yieldItem.marketPricePerUnit}
                        onChange={(e) => handleBulkYieldChange(index, 'marketPricePerUnit', Number(e.target.value))}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        Est. Monthly: Ksh {(yieldItem.dailyAvgYield * yieldItem.marketPricePerUnit * 30).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="error" onClick={() => handleRemoveBulkYield(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>Bulk Addition Details</Divider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Quantity"
              value={bulkAnimalData.quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setBulkAnimalData({ ...bulkAnimalData, quantity: isNaN(val) || val < 1 ? 1 : val });
              }}
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Name Prefix"
              value={bulkAnimalData.prefix}
              onChange={(e) => handleBulkInputChange('prefix', e.target.value)}
              placeholder="E.g., Cow, Bull, Animal"
              helperText="Prefix for auto-generated names"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Starting Number"
              value={bulkAnimalData.startingNumber}
              onChange={(e) => handleBulkInputChange('startingNumber', parseInt(e.target.value, 10) || 1)}
              inputProps={{ min: 1 }}
              helperText="For naming: Prefix + Number"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={bulkAnimalData.notes}
              onChange={(e) => handleBulkInputChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSaveBulkAnimals} 
          variant="contained" 
          color="primary"
          disabled={!bulkAnimalData.animalType || !bulkAnimalData.breed || bulkAnimalData.quantity < 1}
        >
          Add Animals
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAnimalDialog;
